import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  MenuItem
} from '@mui/material';
import {
  TrendingUp,
  AccountBalance,
  Category,
  Inventory,
  FiberManualRecord
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { dashboardAPI, siteAPI } from '../services/api';
import { getTopMaterialsByDepartment } from '../services/settingsAPI';
import { useAuth } from '../context/AuthContext';

const DashboardHome = () => {
  const { user } = useAuth();
  const isSupervisor = user?.role === 'supervisor';
  
  const [stats, setStats] = useState(null);
  const [spendingBySite, setSpendingBySite] = useState([]);
  const [topMaterials, setTopMaterials] = useState({ civil: [], electrical: [] });
  const [recentActivities, setRecentActivities] = useState([]);
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboardData();
    fetchSites();
  }, []);

  useEffect(() => {
    if (sites.length > 0) {
      fetchTopMaterials(selectedSite || null);
    }
  }, [selectedSite]);

  const fetchSites = async () => {
    try {
      const response = await siteAPI.getAll();
      setSites(response.data.data?.sites || []);
    } catch (err) {
      console.error('Error fetching sites:', err);
    }
  };

  const fetchTopMaterials = async (siteId) => {
    try {
      const response = await getTopMaterialsByDepartment(10, siteId);
      setTopMaterials({
        civil: response.data?.civil || [],
        electrical: response.data?.electrical || []
      });
    } catch (err) {
      console.error('Error fetching top materials:', err);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      if (isSupervisor) {
        // Supervisor-specific dashboard
        const response = await dashboardAPI.getSupervisorDashboard();
        const data = response.data.data;
        
        setStats(data.stats || {});
        setSpendingBySite(data.sites || []);
        setRecentActivities(data.recentActivities || []);
      } else {
        // Admin/Manager dashboard
        const [statsRes, spendingRes, activitiesRes] = await Promise.all([
          dashboardAPI.getStats(),
          dashboardAPI.getSpendingBySite(),
          dashboardAPI.getRecentActivities(8)
        ]);

        setStats(statsRes.data.data);
        setSpendingBySite(spendingRes.data.data?.sites || []);
        setRecentActivities(activitiesRes.data.data?.activities || []);
      }
      
      // Fetch top materials after sites are loaded
      await fetchTopMaterials(null);
    } catch (err) {
      console.error('Dashboard error:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  // Stat Card Component
  const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
    <Card sx={{ 
      height: '100%', 
      background: 'white',
      border: '1px solid #F0F0F0',
      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
      transition: 'all 0.3s ease',
      '&:hover': {
        boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
        transform: 'translateY(-2px)'
      }
    }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
              {title}
            </Typography>
            <Typography variant="h3" fontWeight="bold" sx={{ my: 1.5, color: '#1A1A1A' }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box sx={{ 
            p: 1.5, 
            borderRadius: 2, 
            bgcolor: `${color}15`,
          }}>
            <Icon sx={{ fontSize: 36, color }} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  // Prepare chart data
  const totalCivilSpending = spendingBySite.reduce((sum, site) => sum + (site.civilSpending || 0), 0);
  const totalElectricalSpending = spendingBySite.reduce((sum, site) => sum + (site.electricalSpending || 0), 0);

  const departmentData = [
    { name: 'Civil', value: totalCivilSpending || 1, display: `₹${(totalCivilSpending / 10000000).toFixed(2)}Cr` },
    { name: 'Electrical', value: totalElectricalSpending || 1, display: `₹${(totalElectricalSpending / 10000000).toFixed(2)}Cr` }
  ];

  const COLORS = {
    Civil: '#64A8ED',
    Electrical: '#C51668'
  };

  // Site spending bar chart data
  const siteSpendingData = spendingBySite
    .sort((a, b) => b.totalSpent - a.totalSpent)
    .slice(0, 6)
    .map(site => ({
      name: site.siteName.length > 12 ? site.siteName.substring(0, 12) + '...' : site.siteName,
      spending: site.totalSpent / 100000,
      fullName: site.siteName
    }));

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {isSupervisor ? 'Overview of your assigned sites' : 'Overview of your material tracking system'}
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={isSupervisor ? "My Sites" : "Total Sites"}
            value={stats?.sites?.total || 0}
            icon={AccountBalance}
            color="#64A8ED"
            subtitle={`${stats?.sites?.active || 0} active`}
          />
        </Grid>
        {!isSupervisor && (
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Users"
              value={stats?.users?.total || 0}
              icon={Category}
              color="#C51668"
              subtitle={`${stats?.users?.active || 0} active`}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Material Entries"
            value={stats?.materials?.totalEntries || 0}
            icon={Inventory}
            color="#FB9637"
            subtitle="Total transactions"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Spending"
            value={`₹${((stats?.spending?.total || 0) / 10000000).toFixed(2)}Cr`}
            icon={TrendingUp}
            color="#4CAF50"
            subtitle={isSupervisor ? "In your sites" : "Across all sites"}
          />
        </Grid>
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Spending by Department - Pie Chart */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: 400, border: '1px solid #F0F0F0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Spending by Department
            </Typography>
            <ResponsiveContainer width="100%" height="85%">
              <PieChart>
                <Pie
                  data={departmentData}
                  cx="50%"
                  cy="50%"
                  innerRadius={70}
                  outerRadius={110}
                  paddingAngle={5}
                  dataKey="value"
                  label={({ display }) => display}
                >
                  {departmentData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[entry.name]} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value, name, props) => props.payload.display}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Spending by Site - Bar Chart */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, height: 400, border: '1px solid #F0F0F0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Spending by Site (in Lakhs)
            </Typography>
            <ResponsiveContainer width="100%" height="85%">
              <BarChart data={siteSpendingData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                <XAxis 
                  dataKey="name" 
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  style={{ fontSize: '0.75rem' }}
                />
                <YAxis 
                  label={{ value: '₹ (Lakhs)', angle: -90, position: 'insideLeft' }}
                  style={{ fontSize: '0.75rem' }}
                />
                <Tooltip 
                  formatter={(value) => [`₹${(value * 100000).toLocaleString()}`, 'Spending']}
                  labelFormatter={(label) => {
                    const site = siteSpendingData.find(s => s.name === label);
                    return site?.fullName || label;
                  }}
                />
                <Legend />
                <Bar dataKey="spending" fill="#64A8ED" radius={[8, 8, 0, 0]} name="Spending" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Top 10 Materials by Department */}
      <Paper sx={{ p: 3, mb: 3, border: '1px solid #F0F0F0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" fontWeight="bold">
            Top 10 Materials by Department
          </Typography>
          <TextField
            select
            size="small"
            label="Filter by Site"
            value={selectedSite}
            onChange={(e) => setSelectedSite(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">All Sites</MenuItem>
            {sites.map((site) => (
              <MenuItem key={site.id} value={site.id}>
                {site.siteName}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Grid container spacing={3}>
          {/* Civil Department */}
          <Grid item xs={12} md={6}>
            <Box sx={{ 
              p: 2, 
              border: '2px solid #64A8ED', 
              borderRadius: 2,
              bgcolor: '#64A8ED05'
            }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, color: '#64A8ED' }}>
                Civil Department ({topMaterials.civil.length} materials)
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Material</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Quantity</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Spent</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topMaterials.civil.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                            No materials found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      topMaterials.civil.map((material, index) => (
                        <TableRow key={index} sx={{ '&:hover': { bgcolor: '#64A8ED10' } }}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {material.materialName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {material.unit}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {material.totalQuantity.toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold" color="#64A8ED">
                              ₹{material.totalSpent.toLocaleString()}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Grid>

          {/* Electrical Department */}
          <Grid item xs={12} md={6}>
            <Box sx={{ 
              p: 2, 
              border: '2px solid #C51668', 
              borderRadius: 2,
              bgcolor: '#C5166805'
            }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, color: '#C51668' }}>
                Electrical Department ({topMaterials.electrical.length} materials)
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Material</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Quantity</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Spent</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topMaterials.electrical.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                            No materials found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      topMaterials.electrical.map((material, index) => (
                        <TableRow key={index} sx={{ '&:hover': { bgcolor: '#C5166810' } }}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {material.materialName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {material.unit}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {material.totalQuantity.toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold" color="#C51668">
                              ₹{material.totalSpent.toLocaleString()}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Recent Activities */}
      <Paper sx={{ p: 3, border: '1px solid #F0F0F0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
          Recent Activities
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Time</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Activity</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Site</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Material</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Quantity</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentActivities.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      No recent activities
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                recentActivities.map((activity, index) => (
                  <TableRow 
                    key={activity.id || index}
                    sx={{ 
                      '&:hover': { bgcolor: '#FAFAFA' },
                      transition: 'background-color 0.2s'
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FiberManualRecord sx={{ fontSize: 8, color: '#64A8ED' }} />
                        <Typography variant="caption" color="text.secondary">
                          {new Date(activity.createdAt || activity.date).toLocaleDateString('en-IN', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={activity.type || activity.action || 'Material Entry'}
                        size="small"
                        sx={{ 
                          bgcolor: '#64A8ED15',
                          color: '#64A8ED',
                          fontWeight: 600,
                          fontSize: '0.7rem'
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {activity.siteName || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {activity.materialName || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {activity.quantity ? `${activity.quantity} ${activity.unit || ''}` : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold" color="primary">
                        ₹{(activity.amount || activity.totalAmount || 0).toLocaleString()}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default DashboardHome;